import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaPlayCircle,
  FaSearch,
} from "react-icons/fa";
import Header from "components/Header";
import Footer from "components/Footer";
import playerImage from "../../assets/img/pictures/PP.png";

const PLAYERS_PER_PAGE = 6;

const players = [
  ...Array.from({ length: 6 }, (_, index) => ({
    id: index + 1,
    name: "George Abban",
    age: 15,
    role: "Center Forward",
    nationality: "Ghanaian",
    position: "Forward",
    image: playerImage,
  })),
];

const ageBands = ["All", "U16", "U18", "U21", "Senior"];
const positionOptions = ["All", "Goalkeeper", "Defender", "Midfielder", "Forward"];

const matchesAgeBand = (age, band) => {
  if (band === "All") return true;
  if (band === "U16") return age <= 16;
  if (band === "U18") return age <= 18;
  if (band === "U21") return age <= 21;
  if (band === "Senior") return age > 21;
  return true;
};

const Directory = () => {
  const [selectedPosition, setSelectedPosition] = useState("All");
  const [selectedAgeBand, setSelectedAgeBand] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const filteredPlayers = useMemo(() => {
    return players.filter((player) => {
      const byPosition =
        selectedPosition === "All" || player.position === selectedPosition;
      const byAge = matchesAgeBand(player.age, selectedAgeBand);
      const byName = player.name.toLowerCase().includes(searchTerm.toLowerCase());

      return byPosition && byAge && byName;
    });
  }, [selectedPosition, selectedAgeBand, searchTerm]);

  const pageCount = Math.max(1, Math.ceil(filteredPlayers.length / PLAYERS_PER_PAGE));

  const paginatedPlayers = useMemo(() => {
    const start = (currentPage - 1) * PLAYERS_PER_PAGE;
    return filteredPlayers.slice(start, start + PLAYERS_PER_PAGE);
  }, [filteredPlayers, currentPage]);

  const pageNumbers = useMemo(() => {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }, [pageCount]);

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen relative">
      <div className="fixed inset-0 z-0 bg-gradient-to-br from-blue-900/95 via-blue-900/90 to-black/95" />

      <div className="relative z-10">
        <Header />

        <main className="pt-20">
        <header className="relative h-[360px] overflow-hidden bg-gradient-to-br from-blue-900/95 via-blue-900/90 to-black/95 md:h-[420px]">

          <div className="relative mx-auto flex h-full max-w-7xl items-center px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="mb-4 inline-flex rounded-sm bg-green-300 px-3 py-1 text-xs font-bold uppercase tracking-widest text-navy-900">
                Elite Talent Pool
              </span>
              <h1 className="mb-6 text-4xl font-extrabold uppercase leading-tight text-white sm:text-5xl">
                Meet Our <span className="text-green-300">Talent</span>
              </h1>
              <p className="text-lg text-blue-100">
                Discover the next generation of professional athletes. Explore profiles of high-potential players currently enrolled in the Kempshot program.
              </p>
            </div>
          </div>

          <div className="absolute bottom-8 right-8 hidden rounded-lg border-l-4 border-green-300 bg-navy-900/60 px-6 py-3 backdrop-blur-sm lg:block">
            <p className="text-3xl font-bold text-white">120+ Players</p>
            <p className="text-sm font-semibold uppercase tracking-widest text-green-200">Pro-Ready Profiles</p>
          </div>
        </header>

        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-500">
                Filter By
              </span>

              <div className="relative">
                <select
                  value={selectedPosition}
                  onChange={handleFilterChange(setSelectedPosition)}
                  className="w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-4 py-3 pr-10 text-sm font-semibold text-navy-900 focus:border-blue-500 focus:outline-none sm:min-w-[190px]"
                >
                  {positionOptions.map((option) => (
                    <option key={option} value={option}>
                      Position: {option}
                    </option>
                  ))}
                </select>
                <FaChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>

              <div className="relative">
                <select
                  value={selectedAgeBand}
                  onChange={handleFilterChange(setSelectedAgeBand)}
                  className="w-full appearance-none rounded-md border border-gray-200 bg-gray-50 px-4 py-3 pr-10 text-sm font-semibold text-navy-900 focus:border-blue-500 focus:outline-none sm:min-w-[160px]"
                >
                  {ageBands.map((option) => (
                    <option key={option} value={option}>
                      Age: {option}
                    </option>
                  ))}
                </select>
                <FaChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="relative w-full md:w-80">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search player name..."
                className="w-full rounded-md border border-gray-200 bg-gray-50 py-3 pl-11 pr-4 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {paginatedPlayers.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white p-14 text-center shadow-sm">
              <p className="text-xl font-bold text-navy-900">No players found</p>
              <p className="mt-2 text-gray-500">Try changing your filters or search keyword.</p>
            </div>
          ) : (
            <section className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {paginatedPlayers.map((player) => (
                <article
                  key={player.id}
                  className="group flex h-full flex-col overflow-hidden border border-gray-200 bg-white shadow-sm transition hover:-translate-y-2 hover:shadow-xl"
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-gray-100">
                    <img
                      src={player.image}
                      alt={player.name}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-sm bg-navy-900 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                      {player.position}
                    </span>
                  </div>

                  <div className="flex flex-grow flex-col p-5">
                    <h3 className="mb-1 text-2xl font-bold text-navy-900">{player.name}</h3>
                    <p className="mb-4 text-sm text-gray-500">{player.nationality} | Age: {player.age} | {player.role}</p>

                    <div className="mt-auto space-y-3">
                      <button
                        type="button"
                        className="flex w-full items-center justify-center gap-2 rounded-md bg-red-600 py-3 text-xs font-bold uppercase tracking-wider text-white transition hover:bg-red-700"
                      >
                        <FaPlayCircle />
                        Watch Highlights
                      </button>

                      <Link
                        to={`/auth/directory/${player.id}`}
                        className="flex w-full items-center justify-center gap-2 rounded-md border border-navy-900 py-3 text-xs font-bold uppercase tracking-wider text-navy-900 transition hover:bg-navy-900 hover:text-white"
                      >
                        View Profile <FaArrowRight />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </section>
          )}

          <div className="mt-14 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 text-gray-500 transition hover:border-navy-900 hover:text-navy-900 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FaChevronLeft />
            </button>

            {pageNumbers.map((number) => (
              <button
                key={number}
                type="button"
                onClick={() => setCurrentPage(number)}
                className={`h-10 w-10 rounded-md border text-sm font-bold transition ${
                  currentPage === number
                    ? "border-navy-900 bg-navy-900 text-white"
                    : "border-gray-300 text-gray-700 hover:border-navy-900"
                }`}
              >
                {number}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(pageCount, prev + 1))}
              disabled={currentPage === pageCount}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 text-gray-500 transition hover:border-navy-900 hover:text-navy-900 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FaChevronRight />
            </button>
          </div>
        </section>

        <section className="bg-green-300 py-20">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="text-4xl font-extrabold uppercase text-green-900">Are You a Scout?</h2>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-green-900/90">
              Access detailed performance metrics, physical data, and psychological assessments of top talents by joining our official Scout Network.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/auth/register-scout"
                className="rounded-md bg-navy-900 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition hover:bg-navy-700"
              >
                Request Scout Access
              </Link>
              <Link
                to="/auth/programs"
                className="rounded-md border-2 border-navy-900 px-8 py-4 text-sm font-bold uppercase tracking-widest text-navy-900 transition hover:bg-navy-900 hover:text-white"
              >
                Download Prospectus
              </Link>
            </div>
          </div>
        </section>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default Directory;
